import type { ScheduleResult } from "@planerius/contracts/cabinet"

import { buildJournalDay } from "../../journal/journal-model"
import { formatTimeOfDay } from "../../lib/format"

/**
 * ПЕЧАТЬ РАСПИСАНИЯ (ТЗ 02, §2.12; макет П2, 2-2.12).
 *
 * Салоны печатают день на чёрно-белом принтере и вешают у стойки. Поэтому всё, что на
 * экране различается цветом, здесь различается ЗНАКОМ: ✓ завершён, ▸ в работе, ? ждёт
 * подтверждения, — не пришёл, ■ перерыв и блокировка; подтверждённая — без знака.
 * Отменённые не печатаются, неявки — с отметкой, перерывы и отпуска — наравне с записями:
 * иначе распечатка врёт.
 *
 * Сетка собирается из той же модели дня, что журнал (buildJournalDay): колонки в том же
 * порядке, те же границы смен. Список мастера — из записей дня напрямую: в карточке
 * журнала нет ни комментария, ни телефона.
 *
 * ТЕЛЕФОНЫ — только в списке и только по осознанному переключателю (по умолчанию выключен):
 * распечатка с телефонами — вынос персональных данных за пределы системы. В сетке их нет
 * никогда. Номер есть только у того, кому он открыт целиком (phoneAccess = shown): мастер,
 * получающий номер по действию с записью в журнал открытий, печатать телефоны не может.
 */

export type PrintMode = "grid" | "list"

export type PrintKind = "done" | "work" | "pending" | "noshow" | "confirmed" | "busy" | "cont"

export const PRINT_MARKS: Readonly<Record<PrintKind, string>> = {
  done: "✓ ",
  work: "▸ ",
  pending: "? ",
  noshow: "— ",
  confirmed: "",
  busy: "■ ",
  cont: "",
}

const STATUS_KIND: Readonly<Record<string, PrintKind>> = {
  completed: "done",
  in_progress: "work",
  pending: "pending",
  no_show: "noshow",
  confirmed: "confirmed",
}

const STATUS_WORDS: Readonly<Record<string, string>> = {
  completed: "завершён ✓",
  in_progress: "в работе ▸",
  pending: "ждёт ?",
  no_show: "не пришёл —",
  confirmed: "подтверждена",
}

/** Сетка помещается на лист A4 альбомной, пока колонок не больше восьми; дальше — следующий лист. */
export const GRID_COLUMNS_PER_SHEET = 8
/** Строк списка на лист A4 книжной: шапка, заголовок мастера, таблица 10.5 px. */
export const LIST_ROWS_PER_SHEET = 28

export interface PrintGridItem {
  readonly kind: PrintKind
  /** Клиент или подпись перерыва; у начала не на целом часе — со временем: «10:15 Иванов». */
  readonly title: string
  readonly sub: string
}

export interface PrintGridColumn {
  readonly name: string
  /** «10–20», «10:30–19» или «выходной». */
  readonly work: string
  /** По часу шкалы: что начинается или продолжается в этот час, и рабочий ли он. */
  readonly cells: readonly { readonly items: readonly PrintGridItem[]; readonly off: boolean }[]
}

export interface PrintGrid {
  readonly hours: readonly number[]
  readonly sheets: readonly (readonly PrintGridColumn[])[]
}

function hourLabel(minute: number): string {
  const h = Math.floor(minute / 60)
  const m = minute % 60
  return m === 0 ? String(h) : `${h}:${String(m).padStart(2, "0")}`
}

function clock(minute: number): string {
  return `${String(Math.floor(minute / 60)).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`
}

export function buildPrintGrid(result: ScheduleResult, date: string): PrintGrid {
  const day = buildJournalDay(result, date, { includeCancelled: false })
  const first = Math.floor(day.window.openMinute / 60)
  const last = Math.ceil(day.window.closeMinute / 60)
  const hours = Array.from({ length: Math.max(1, last - first) }, (_, index) => first + index)

  const columns = day.columns.map((column): PrintGridColumn => {
    const entries = [
      ...column.cards.map((card) => ({
        start: card.startMinute,
        end: card.endMinute,
        kind: STATUS_KIND[card.status] ?? "confirmed",
        name: card.clientName,
        sub: card.extraServices > 0 ? `${card.serviceName} + ещё ${card.extraServices}` : card.serviceName,
      })),
      // Буфер и уборка — продолжение соседней записи, а не событие дня.
      ...column.busy
        .filter((block) => !block.isSystem)
        .map((block) => ({ start: block.startMinute, end: block.endMinute, kind: "busy" as const, name: block.label, sub: "" })),
    ].toSorted((a, b) => a.start - b.start)

    const cells = hours.map((hour) => {
      const from = hour * 60
      const to = from + 60
      const items = entries
        .filter((entry) => entry.start < to && entry.end > from)
        .map((entry): PrintGridItem => {
          if (entry.start < from) {
            // Продолжение длинной записи: имя мелко, чтобы клетка не выглядела свободной.
            return { kind: "cont", title: `… ${entry.name}`, sub: "" }
          }
          const title = entry.start === from ? entry.name : `${clock(entry.start)} ${entry.name}`
          return { kind: entry.kind, title, sub: entry.sub }
        })
      const off =
        column.workStartMinute === null ||
        column.workEndMinute === null ||
        to <= column.workStartMinute ||
        from >= column.workEndMinute
      return { items, off }
    })

    const work =
      column.workStartMinute === null || column.workEndMinute === null
        ? "выходной"
        : `${hourLabel(column.workStartMinute)}–${hourLabel(column.workEndMinute)}`

    return { name: column.employee.displayName, work, cells }
  })

  const sheets: PrintGridColumn[][] = []
  for (let index = 0; index < columns.length; index += GRID_COLUMNS_PER_SHEET) {
    sheets.push(columns.slice(index, index + GRID_COLUMNS_PER_SHEET))
  }
  return { hours, sheets: sheets.length === 0 ? [[]] : sheets }
}

export interface PrintListRow {
  readonly time: string
  readonly client: string
  readonly phone: string
  readonly service: string
  readonly minutes: number
  readonly comment: string
  readonly status: string
  readonly kind: PrintKind
}

export interface PrintList {
  /** «Анна Соколова · барбер · смена 10:00–20:00». */
  readonly title: string
  readonly sheets: readonly (readonly PrintListRow[])[]
  /** Есть ли у кого-то из клиентов номер, открытый этому человеку целиком. */
  readonly phonesAvailable: boolean
}

export function buildPrintList(
  result: ScheduleResult,
  date: string,
  employeeId: string,
  withPhones: boolean,
): PrintList {
  const timeZone = result.branch.timezone
  const employee = result.employees.find((item) => item.id === employeeId)
  const workday = result.workdays.find((item) => item.employeeId === employeeId && item.date === date)
  const onDay = (instant: string): boolean =>
    new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(
      new Date(instant),
    ) === date

  const appointments = result.appointments.filter(
    (item) => item.employeeId === employeeId && item.status !== "cancelled" && onDay(item.startAt),
  )
  const phonesAvailable = appointments.some((item) => item.client.phoneAccess === "shown" && item.client.phone !== null)

  const rows: (PrintListRow & { readonly at: string })[] = [
    ...appointments.map((item) => {
      const [first, ...rest] = item.services
      return {
        at: item.startAt,
        time: formatTimeOfDay(item.startAt, timeZone),
        client: item.client.displayName,
        phone: withPhones && item.client.phoneAccess === "shown" ? (item.client.phone ?? "") : "",
        service: first === undefined ? "" : rest.length === 0 ? first.name : `${first.name} + ещё ${rest.length}`,
        minutes: Math.round((Date.parse(item.endAt) - Date.parse(item.startAt)) / 60_000),
        comment: item.comment ?? "",
        status: STATUS_WORDS[item.status] ?? "",
        kind: STATUS_KIND[item.status] ?? "confirmed",
      }
    }),
    ...result.busy
      .filter(
        (block) =>
          block.employeeId === employeeId &&
          (block.kind === "break" || block.kind === "blocked" || block.kind === "timeoff") &&
          onDay(block.startAt),
      )
      .map((block) => ({
        at: block.startAt,
        time: formatTimeOfDay(block.startAt, timeZone),
        client:
          block.note?.trim() ||
          (block.kind === "break" ? "Перерыв" : block.kind === "timeoff" ? "Отсутствие" : "Заблокировано"),
        phone: "",
        service: "",
        minutes: Math.round((Date.parse(block.endAt) - Date.parse(block.startAt)) / 60_000),
        comment: "",
        status: "",
        kind: "busy" as const,
      })),
  ].toSorted((a, b) => a.at.localeCompare(b.at))

  const parts = [employee?.displayName ?? "Мастер"]
  if (employee?.positionName) parts.push(employee.positionName.toLowerCase())
  parts.push(
    workday === undefined
      ? "выходной"
      : `смена ${formatTimeOfDay(workday.startAt, timeZone)}–${formatTimeOfDay(workday.endAt, timeZone)}`,
  )

  const sheets: PrintListRow[][] = []
  for (let index = 0; index < rows.length; index += LIST_ROWS_PER_SHEET) {
    sheets.push(rows.slice(index, index + LIST_ROWS_PER_SHEET).map(({ at: _at, ...row }) => row))
  }
  return { title: parts.join(" · "), sheets: sheets.length === 0 ? [[]] : sheets, phonesAvailable }
}

/** «Среда, 16 сентября 2026». */
export function printDateWords(date: string): string {
  const words = new Intl.DateTimeFormat("ru-RU", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${date}T12:00:00Z`))
  // Intl отдаёт «среда, 16 сентября 2026 г.» — без «г.» и с заглавной, как в макете.
  const clean = words.replace(/\s*г\.$/u, "")
  return clean.charAt(0).toUpperCase() + clean.slice(1)
}

/** «напечатано 16.09.2026 13:47» — по часам филиала: лист висит у его стойки. */
export function printedAtWords(now: Date, timeZone: string): string {
  const date = new Intl.DateTimeFormat("ru-RU", { timeZone, day: "2-digit", month: "2-digit", year: "numeric" }).format(now)
  return `напечатано ${date} ${formatTimeOfDay(now, timeZone)}`
}

/** «Печать · 1 лист», «Печать · 3 листа». */
export function sheetsWords(count: number): string {
  const mod10 = count % 10
  const mod100 = count % 100
  const word =
    mod10 === 1 && mod100 !== 11 ? "лист" : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? "листа" : "листов"
  return `Печать · ${count} ${word}`
}
